import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { CustomModal } from "components/CustomModal";
import { DividerHorizontal } from "components/Divider";
import { FormRowLabel } from "components/FormRowLabel";
import { Message } from "components/Message";
import { FileUploadRow } from "features/shared/components/FileUploadRow";
import { StatusIndicator } from "features/shared/components/StatusIndicator";
import { useTranslation } from "react-i18next";

import type { ModalActionButton, SubmitInfo, SubmitStepInfo } from "./types";

type ActionInfoModalProps = {
  errorMessage?: string;
  modalActionButton: ModalActionButton[];
  open: boolean;
  submitInfo: SubmitInfo;
};

function SingleStepRow({ mode, step }: { mode: "linear" | "spinner"; step: SubmitStepInfo }) {
  return (
    <div className="w-full">
      <Stack className="w-full justify-between gap-3">
        <span className="text-left font-ds-body-semibold-sm">{step.name}</span>
        <Stack className="h-5 min-w-[2rem] justify-center">
          <StatusIndicator className="h-5 w-5" mode={mode} step={step} />
        </Stack>
      </Stack>
      {step.errorMessage && <Message className="" message={step.errorMessage} variant="error" />}
    </div>
  );
}

export const ActionInfoModal = ({ modalActionButton, open, submitInfo }: ActionInfoModalProps) => {
  const { t } = useTranslation();

  return (
    <CustomModal closeIcon={false} height="90%" modalOpen={open} setModalOpen={() => {}} width="50rem">
      <Stack className="h-full flex-col justify-between gap-6">
        <h2 className="font-ds-headline-lg self-start pr-8">{t("AddBuilding.submitModal.title")}</h2>
        <Stack className="w-full  flex-1 flex-col items-start gap-4 overflow-y-auto pr-2">
          {submitInfo.deletedAPIImages.length > 0 && (
            <>
              <FormRowLabel className="self-center" label={t("AddBuilding.submitModal.deletedImagesLabel")} />
              {submitInfo.deletedAPIImages.map((image) => (
                <SingleStepRow key={`img-${image.name}`} mode="spinner" step={image} />
              ))}
              <DividerHorizontal className="mt-2" />
            </>
          )}
          {submitInfo.images.length > 0 && (
            <>
              <FormRowLabel className="self-center" label={t("AddBuilding.submitModal.imagesLabel")} />
              {submitInfo.images.map((image) => (
                <FileUploadRow key={`img-${image.name}`} step={image} />
              ))}
              <DividerHorizontal className="mt-2" />
            </>
          )}
          {submitInfo.files.length > 0 && (
            <>
              <FormRowLabel className="self-center" label={t("AddBuilding.submitModal.filesLabel")} />
              {submitInfo.files.map((file) => (
                <FileUploadRow key={`file-${file.name}`} step={file} />
              ))}
              <DividerHorizontal className="mt-2" />
            </>
          )}
          <FormRowLabel className="self-center" label={t("AddBuilding.submitModal.rows.projectCreationLabel")} />
          <SingleStepRow mode="spinner" step={submitInfo.buildingInfo} />
          <SingleStepRow mode="spinner" step={submitInfo.projectCreation} />
        </Stack>
        {modalActionButton
          .filter((button) => button.show)
          .map((button) => (
            <Button className="" key={button.label} onClick={button.onClick} size="sm" variant="secondary">
              {button.label}
            </Button>
          ))}
      </Stack>
    </CustomModal>
  );
};
